import {
    useState, useMemo, useRef, useEffect,
    ChangeEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { Certification, SortDir, SortField, StatusFilter, ToastState, ViewMode } from "../../types/certificate";
import { ALL_CERTS, PAGE_SIZES } from "../../constants/certificate";
import { ConfirmModal } from "../../components/common/types";
import { CertPlaceholder, fmt, highlight, isExpiringSoon, StatusBadge } from "../../helpers/certificate";
import { CertCard, ViewModal } from "../../components/pages/certificates";
import { SortIcon } from "../../components/common/UI/Icons/SortIcon";
import { IconBtn } from "../../components/common/UI/Button/IconBtn";
import DeleteModal from "../../components/common/Modal/DeleteModal";

export default function AllCertifications() {
    const [certs, setCerts] = useState<Certification[]>(ALL_CERTS);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
    const [sortField, setSortField] = useState<SortField>("issuedDate");
    const [sortDir, setSortDir] = useState<SortDir>("desc");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(9);
    const [viewMode, setViewMode] = useState<ViewMode>("grid");
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [confirmModal, setConfirmModal] = useState<ConfirmModal>({ open: false, id: null, title: "" });
    const [viewCert, setViewCert] = useState<Certification | null>(null);
    const [toast, setToast] = useState<ToastState>({ visible: false, message: "", type: "success" });

    const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const navigate = useNavigate();

    const showToast = (msg: string, type: ToastState["type"] = "success", id?: string, delay?: number) => {
        if (toastTimer.current) clearTimeout(toastTimer.current);
        setToast({ visible: true, message: msg, type });
        toastTimer.current = setTimeout(() => { setToast((t) => ({ ...t, visible: false })); if (delay) navigate("/manage/certification", { state: { id } }); }, delay || 3000);
    };

    // ── Derived ──────────────────────────────────────────────────────────────

    const filtered = useMemo(() => {
        let list = [...certs];
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter((c) =>
                c.title.toLowerCase().includes(q) || c.issuedBy.toLowerCase().includes(q) || c.id.includes(q)
            );
        }
        if (statusFilter !== "all") list = list.filter((c) => c.status === statusFilter);
        list.sort((a, b) => {
            const va = a[sortField] ?? "";
            const vb = b[sortField] ?? "";
            const cmp = String(va).localeCompare(String(vb));
            return sortDir === "asc" ? cmp : -cmp;
        });
        return list;
    }, [certs, search, statusFilter, sortField, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageSlice = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

    useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);

    // ── Selection ────────────────────────────────────────────────────────────

    const allSelected = pageSlice.length > 0 && pageSlice.every((c) => selected.has(c.id));
    const someSelected = selected.size > 0;

    const toggleAll = () => {
        const next = new Set(selected);
        allSelected ? pageSlice.forEach((c) => next.delete(c.id)) : pageSlice.forEach((c) => next.add(c.id));
        setSelected(next);
    };
    const toggle = (id: string) => {
        const next = new Set(selected);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelected(next);
    };

    // ── Actions ──────────────────────────────────────────────────────────────

    const toggleSort = (f: SortField) => {
        if (sortField === f) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortField(f); setSortDir("asc"); }
    };

    const handleDeleteRequest = (id: string, title: string) =>
        setConfirmModal({ open: true, id, title });

    const handleDeleteConfirm = () => {
        if (!confirmModal.id) return;
        setCerts((prev) => prev.filter((c) => c.id !== confirmModal.id));
        setSelected((prev) => { const n = new Set(prev); n.delete(confirmModal.id!); return n; });
        setConfirmModal({ open: false, id: null, title: "" });
        showToast("Certification deleted");
    };

    const handleBulkDelete = () => {
        setCerts((prev) => prev.filter((c) => !selected.has(c.id)));
        showToast(`${selected.size} certification${selected.size > 1 ? "s" : ""} deleted`);
        setSelected(new Set());
    };

    const handleStatusChange = (id: string, status: Certification["status"]) => {
        setCerts((prev) => prev.map((c) => c.id === id ? { ...c, status } : c));
        showToast(`Status updated to "${status}"`);
    };

    // ── Pagination ────────────────────────────────────────────────────────────

    const pageNums = (): (number | "…")[] => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        if (safePage <= 4) return [1, 2, 3, 4, 5, "…", totalPages];
        if (safePage >= totalPages - 3) return [1, "…", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        return [1, "…", safePage - 1, safePage, safePage + 1, "…", totalPages];
    };

    // ── Stats ─────────────────────────────────────────────────────────────────

    const stats = useMemo(() => ({
        total: certs.length,
        active: certs.filter((c) => c.status === "active").length,
        expired: certs.filter((c) => c.status === "expired").length,
        pending: certs.filter((c) => c.status === "pending").length,
        expiring: certs.filter((c) => isExpiringSoon(c.expiryDate)).length,
    }), [certs]);

    const btnStyle = (active: boolean, danger = false): React.CSSProperties => ({
        width: 30, height: 30, borderRadius: 7, cursor: "pointer", display: "flex",
        alignItems: "center", justifyContent: "center", fontSize: active ? 11 : 13,
        border: `1px solid ${active ? "rgba(191,61,12,0.35)" : "var(--border)"}`,
        background: active ? "var(--or-dim)" : "transparent",
        color: active ? "var(--or)" : "var(--text2)",
        fontWeight: active ? 600 : 400, transition: "all 0.15s",
    });

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        // :root {
        //   --or:#BF3D0C; --or2:#E8593C; --or-dim:rgba(191,61,12,0.10);
        //   --text:#e8e6de; --text2:#b4b2a9; --text3:#6a6a65;
        //   --border:rgba(255,255,255,0.08); --card:rgba(255,255,255,0.025);
        //   --row-hover:rgba(255,255,255,0.025); --input-bg:rgba(255,255,255,0.035);
        //   --section-header:rgba(255,255,255,0.02); --surface:#111110;
        // }
        body { margin:0; font-family:'DM Sans',sans-serif; }
        input::placeholder { color:var(--text3); }
        ::-webkit-scrollbar { width:5px; height:5px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.08); border-radius:4px; }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes toastIn     { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes modalIn     { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }
        .ac-row { transition:background 0.12s; }
        .ac-row:hover { background:var(--row-hover) !important; }
        .ac-th { user-select:none; cursor:pointer; }
        .ac-th:hover { color:var(--text) !important; }
        .ac-pgbtn { transition:all 0.15s; }
        .ac-pgbtn:hover:not(:disabled) { background:var(--or-dim)!important; color:var(--or)!important; border-color:rgba(191,61,12,0.3)!important; }
        .ac-card-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:16px; }
        @media (max-width: 768px) { .cert-header { display: block !important; } .cert-stat { grid-template-columns:repeat(2,1fr) !important; } }
        @media (max-width: 480px) {.cert-stat { grid-template-columns:repeat(1,1fr) !important; } }
      `}</style>

            {/* Toast */}
            {toast.visible && (
                <div style={{
                    position: "fixed", top: 20, right: 20, zIndex: 600,
                    background: toast.type === "success" ? "#1a2e1a" : "#2e1a1a",
                    border: `1px solid ${toast.type === "success" ? "rgba(90,200,90,0.25)" : "rgba(224,82,82,0.25)"}`,
                    borderRadius: 10, padding: "12px 18px", display: "flex", alignItems: "center", gap: 10,
                    animation: "toastIn 0.25s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                }}>
                    <span style={{ fontSize: 14, color: toast.type === "success" ? "#9be09b" : "#e08b8b" }}>
                        {toast.type === "success" ? "✓" : "✕"}&nbsp;&nbsp;{toast.message}
                    </span>
                </div>
            )}

            <DeleteModal
                modal={confirmModal}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setConfirmModal({ open: false, id: null, title: "" })}
            />
            <ViewModal cert={viewCert} onClose={() => setViewCert(null)} />

            <div style={{ minHeight: "100vh", padding: "32px 28px" }}>
                <div style={{ maxWidth: 1280, margin: "0 auto", animation: "fadeSlideIn 0.3s ease" }}>

                    {/* ── Page header ── */}
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }} className="cert-header">
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--or)", marginBottom: 4 }}>
                                Content
                            </div>
                            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "var(--text)", letterSpacing: "-0.4px" }}>
                                All Certifications
                            </h1>
                            <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--text3)" }}>
                                {certs.length} certifications · {stats.expiring > 0 && (
                                    <span style={{ color: "#f5a623" }}>{stats.expiring} expiring within 90 days</span>
                                )}
                            </p>
                        </div>
                        <button
                            style={{
                                display: "flex", alignItems: "center", gap: 7,
                                padding: "10px 18px", borderRadius: 9,
                                border: "1px solid rgba(191,61,12,0.35)",
                                background: "linear-gradient(135deg,#BF3D0C,#E8593C)",
                                color: "#fff", fontSize: 14, fontWeight: 500, cursor: "pointer",
                                fontFamily: "'DM Sans',sans-serif", boxShadow: "0 2px 12px rgba(191,61,12,0.25)",
                                marginTop: 10
                            }}
                            onClick={() => navigate('/manage/certification')}
                        >
                            <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add Certification
                        </button>
                    </div>

                    {/* ── Stat cards ── */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 20 }} className="cert-stat">
                        {[
                            { label: "Total", value: stats.total, color: "var(--text2)", dot: "var(--text3)" },
                            { label: "Active", value: stats.active, color: "#6ddc6d", dot: "#6ddc6d" },
                            { label: "Expired", value: stats.expired, color: "#e05252", dot: "#e05252" },
                            { label: "Pending", value: stats.pending, color: "#f5a623", dot: "#f5a623" },
                            { label: "Expiring soon", value: stats.expiring, color: "#f5a623", dot: "#f5a623" },
                        ].map((s) => (
                            <div key={s.label} style={{
                                background: "var(--card)", border: "1px solid var(--border)",
                                borderRadius: 12, padding: "14px 18px",
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                            }}>
                                <div>
                                    <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 4 }}>{s.label}</div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: s.color, lineHeight: 1 }}>{s.value}</div>
                                </div>
                                <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.dot, opacity: 0.6 }} />
                            </div>
                        ))}
                    </div>

                    {/* ── Toolbar ── */}
                    <div style={{
                        background: "var(--card)", border: "1px solid var(--border)",
                        borderRadius: 14, overflow: "hidden",
                    }}>
                        <div style={{
                            display: "flex", alignItems: "center", gap: 12,
                            padding: "14px 16px", borderBottom: "1px solid var(--border)", flexWrap: "wrap",
                        }}>
                            {/* Search */}
                            <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
                                <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--text3)", fontSize: 14, pointerEvents: "none" }}>⌕</span>
                                <input
                                    value={search}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                                    placeholder="Search by title, issuer, or ID…"
                                    style={{
                                        width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)",
                                        borderRadius: 8, padding: "8px 12px 8px 32px", fontSize: 13, color: "var(--text)",
                                        outline: "none", fontFamily: "'DM Sans',sans-serif",
                                    }}
                                />
                                {search && (
                                    <button onClick={() => setSearch("")} style={{
                                        position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                                        background: "none", border: "none", color: "var(--text3)", cursor: "pointer", fontSize: 14,
                                    }}>×</button>
                                )}
                            </div>

                            {/* Status filter */}
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                {(["all", "active", "pending", "expired"] as StatusFilter[]).map((s) => (
                                    <button key={s} onClick={() => setStatusFilter(s)} style={{
                                        fontSize: 12, padding: "6px 12px", borderRadius: 7, cursor: "pointer",
                                        border: `1px solid ${statusFilter === s ? "rgba(191,61,12,0.3)" : "var(--border)"}`,
                                        background: statusFilter === s ? "var(--or-dim)" : "transparent",
                                        color: statusFilter === s ? "var(--or)" : "var(--text3)",
                                        fontFamily: "'DM Sans',sans-serif", textTransform: "capitalize",
                                        transition: "all 0.15s",
                                    }}>{s === "all" ? "All status" : s}</button>
                                ))}
                            </div>

                            {/* Bulk delete */}
                            {someSelected && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{ fontSize: 12, color: "var(--text3)" }}>{selected.size} selected</span>
                                    <button onClick={handleBulkDelete} style={{
                                        fontSize: 12, padding: "6px 12px", borderRadius: 7, cursor: "pointer",
                                        border: "1px solid rgba(224,82,82,0.3)", background: "rgba(224,82,82,0.08)",
                                        color: "#e05252", fontFamily: "'DM Sans',sans-serif",
                                    }}>Delete selected</button>
                                </div>
                            )}

                            {/* View toggle + results */}
                            <div style={{ display: "flex", gap: 4, marginLeft: someSelected ? 0 : "auto" }}>
                                <button
                                    onClick={() => setViewMode("grid")}
                                    style={btnStyle(viewMode === "grid")}
                                    title="Grid view"
                                >
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
                                        <rect x="0" y="0" width="6" height="6" rx="1" /><rect x="10" y="0" width="6" height="6" rx="1" />
                                        <rect x="0" y="10" width="6" height="6" rx="1" /><rect x="10" y="10" width="6" height="6" rx="1" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setViewMode("table")}
                                    style={btnStyle(viewMode === "table")}
                                    title="Table view"
                                >
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="1" y="1" width="14" height="14" rx="1" />
                                        <line x1="1" y1="5" x2="15" y2="5" />
                                        <line x1="1" y1="9" x2="15" y2="9" />
                                        <line x1="1" y1="13" x2="15" y2="13" />
                                        <line x1="5" y1="5" x2="5" y2="15" />
                                    </svg>
                                </button>
                            </div>
                            <span style={{ fontSize: 12, color: "var(--text3)", whiteSpace: "nowrap" }}>
                                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                            </span>
                        </div>

                        {/* ── Grid view ── */}
                        {viewMode === "grid" && (
                            <div style={{ padding: 16 }}>
                                {pageSlice.length === 0 ? (
                                    <div style={{ textAlign: "center", padding: "56px 0", color: "var(--text3)" }}>
                                        <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.3 }}>🏅</div>
                                        <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text2)", marginBottom: 6 }}>
                                            {search ? `No certifications match "${search}"` : "No certifications found"}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="ac-card-grid">
                                        {pageSlice.map((cert, i) => (
                                            <div key={cert.id} style={{ animation: `fadeSlideIn 0.2s ease ${i * 0.03}s both` }}>
                                                <CertCard
                                                    cert={cert} selected={selected.has(cert.id)}
                                                    searchQuery={search}
                                                    onSelect={() => toggle(cert.id)}
                                                    onView={() => setViewCert(cert)}
                                                    onEdit={() => showToast("Opening editor…", 'success', cert.id, 1000)}
                                                    onDelete={() => handleDeleteRequest(cert.id, cert.title)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Table view ── */}
                        {viewMode === "table" && (
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
                                    <thead>
                                        <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                                            <th style={{ width: 44, padding: "11px 16px", textAlign: "center", borderBottom: "1px solid var(--border)" }}>
                                                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                                                    style={{ accentColor: "var(--or)", cursor: "pointer", width: 14, height: 14 }} />
                                            </th>
                                            <th style={{ width: 48, padding: "11px 8px 11px 0", borderBottom: "1px solid var(--border)" }} />
                                            {([
                                                { field: "title" as SortField, label: "Title", w: "auto" },
                                                { field: "issuedBy" as SortField, label: "Issued By", w: 160 },
                                                { field: "status" as SortField, label: "Status", w: 120 },
                                                { field: "issuedDate" as SortField, label: "Issued", w: 100 },
                                                { field: "expiryDate" as SortField, label: "Expires", w: 100 },
                                            ]).map((col) => (
                                                <th key={col.field} className="ac-th"
                                                    onClick={() => toggleSort(col.field)}
                                                    style={{
                                                        width: col.w, padding: "11px 14px", textAlign: "left",
                                                        borderBottom: "1px solid var(--border)",
                                                        fontSize: 11, fontWeight: 600,
                                                        color: sortField === col.field ? "var(--or)" : "var(--text3)",
                                                        textTransform: "uppercase", letterSpacing: "0.8px", whiteSpace: "nowrap",
                                                    }}>
                                                    {col.label}
                                                    <SortIcon active={sortField === col.field} dir={sortDir} />
                                                </th>
                                            ))}
                                            <th style={{ width: 100, padding: "11px 14px", borderBottom: "1px solid var(--border)", fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageSlice.length === 0 ? (
                                            <tr><td colSpan={8} style={{ padding: "56px 24px", textAlign: "center" }}>
                                                <div style={{ fontSize: 14, color: "var(--text3)" }}>No certifications found</div>
                                            </td></tr>
                                        ) : pageSlice.map((cert, idx) => (
                                            <tr key={cert.id} className="ac-row" style={{
                                                background: selected.has(cert.id) ? "rgba(191,61,12,0.06)" : "transparent",
                                                borderBottom: idx < pageSlice.length - 1 ? "1px solid var(--border)" : "none",
                                                animation: `fadeSlideIn 0.2s ease ${idx * 0.025}s both`,
                                            }}>
                                                <td style={{ padding: "12px 16px", textAlign: "center" }}>
                                                    <input type="checkbox" checked={selected.has(cert.id)} onChange={() => toggle(cert.id)}
                                                        style={{ accentColor: "var(--or)", cursor: "pointer", width: 14, height: 14 }} />
                                                </td>
                                                <td style={{ padding: "12px 8px 12px 0" }}>
                                                    {cert.imageUrl
                                                        ? <img src={cert.imageUrl} alt="" style={{ width: 36, height: 36, borderRadius: 6, objectFit: "contain", border: "1px solid var(--border)" }} />
                                                        : <CertPlaceholder size={36} />
                                                    }
                                                </td>
                                                <td style={{ padding: "12px 14px" }}>
                                                    <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)", marginBottom: 2 }}>
                                                        {highlight(cert.title, search)}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "monospace" }}>{cert.id}</div>
                                                </td>
                                                <td style={{ padding: "12px 14px", fontSize: 13, color: "var(--text2)" }}>{cert.issuedBy}</td>
                                                <td style={{ padding: "12px 14px" }}>
                                                    <div style={{ position: "relative", display: "inline-block" }}>
                                                        <StatusBadge status={cert.status} expiry={cert.expiryDate} />
                                                        <select
                                                            value={cert.status}
                                                            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                                handleStatusChange(cert.id, e.target.value as Certification["status"])
                                                            }
                                                            style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
                                                        >
                                                            <option value="active">Active</option>
                                                            <option value="pending">Pending</option>
                                                            <option value="expired">Expired</option>
                                                        </select>
                                                    </div>
                                                </td>
                                                <td style={{ padding: "12px 14px", fontSize: 13, color: "var(--text3)", whiteSpace: "nowrap" }}>{fmt(cert.issuedDate)}</td>
                                                <td style={{
                                                    padding: "12px 14px", fontSize: 13, whiteSpace: "nowrap",
                                                    color: isExpiringSoon(cert.expiryDate) ? "#f5a623" : "var(--text3)"
                                                }}>
                                                    {fmt(cert.expiryDate)}
                                                </td>
                                                <td style={{ padding: "12px 14px" }}>
                                                    <div style={{ display: "flex", gap: 5 }}>
                                                        <IconBtn title="View" onClick={() => setViewCert(cert)}>
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                                                            </svg>
                                                        </IconBtn>
                                                        <IconBtn title="Edit" onClick={() => showToast("Opening editor…", 'success', cert.id, 1000)}>
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                            </svg>
                                                        </IconBtn>
                                                        <IconBtn title="Delete" danger onClick={() => handleDeleteRequest(cert.id, cert.title)}>
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="3 6 5 6 21 6" />
                                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                                <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
                                                            </svg>
                                                        </IconBtn>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Pagination ── */}
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "12px 16px", borderTop: "1px solid var(--border)", flexWrap: "wrap", gap: 10,
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 12, color: "var(--text3)" }}>Per page</span>
                                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{
                                    fontSize: 12, padding: "4px 8px", borderRadius: 6,
                                    border: "1px solid var(--border)", background: "rgba(255,255,255,0.04)",
                                    color: "var(--text2)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", outline: "none",
                                }}>
                                    {PAGE_SIZES.map((n) => (
                                        <option key={n} value={n} style={{ background: "#1a1917" }}>{n}</option>
                                    ))}
                                </select>
                            </div>

                            <span style={{ fontSize: 12, color: "var(--text3)" }}>
                                {filtered.length === 0 ? "0" : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)}`} of {filtered.length}
                            </span>

                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                {[
                                    { label: "«", action: () => setPage(1), disabled: safePage === 1 },
                                    { label: "‹", action: () => setPage((p) => Math.max(1, p - 1)), disabled: safePage === 1 },
                                ].map((b) => (
                                    <button key={b.label} className="ac-pgbtn" onClick={b.action} disabled={b.disabled} style={{
                                        width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)",
                                        background: "transparent", color: b.disabled ? "var(--text3)" : "var(--text2)",
                                        cursor: b.disabled ? "not-allowed" : "pointer", fontSize: 14,
                                        opacity: b.disabled ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>{b.label}</button>
                                ))}

                                {pageNums().map((n, i) =>
                                    n === "…" ? (
                                        <span key={`e${i}`} style={{ width: 30, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>…</span>
                                    ) : (
                                        <button key={n} className="ac-pgbtn" onClick={() => setPage(n as number)} style={{
                                            width: 30, height: 30, borderRadius: 7,
                                            border: `1px solid ${safePage === n ? "rgba(191,61,12,0.35)" : "var(--border)"}`,
                                            background: safePage === n ? "var(--or-dim)" : "transparent",
                                            color: safePage === n ? "var(--or)" : "var(--text2)",
                                            cursor: "pointer", fontSize: 13, fontWeight: safePage === n ? 600 : 400,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>{n}</button>
                                    )
                                )}

                                {[
                                    { label: "›", action: () => setPage((p) => Math.min(totalPages, p + 1)), disabled: safePage === totalPages },
                                    { label: "»", action: () => setPage(totalPages), disabled: safePage === totalPages },
                                ].map((b) => (
                                    <button key={b.label} className="ac-pgbtn" onClick={b.action} disabled={b.disabled} style={{
                                        width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)",
                                        background: "transparent", color: b.disabled ? "var(--text3)" : "var(--text2)",
                                        cursor: b.disabled ? "not-allowed" : "pointer", fontSize: 14,
                                        opacity: b.disabled ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>{b.label}</button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}